import React from "react";
import { motion } from "framer-motion";

export default function EventStaffingKeyFeatures() {
  const features = [
    {
      id: "1",
      numberTitle: "1. Dealer Management",
      desc: "Centralised dealer profiles, skills, rankings, ratings, availability, and assignment history give administrators a complete view of the dealer network.",
      capabilities: ["Profiles", "Ranks", "Game skills", "History"],
      img: "/images/event-staffing/event-staffing-feat-dealer-mgmt.png",
      alt: "Dealer Management Interface",
      imgOnRight: true,
    },
    {
      id: "2",
      numberTitle: "2. Party Creation",
      desc: "Core event details, venue requirements, games, staffing visibility, and dealer instructions are captured in a structured six-step flow that keeps setup consistent.",
      capabilities: ["Party setup", "Venue", "Visibility", "Publishing"],
      img: "/images/event-staffing/event-staffing-feat-party-creation.png",
      alt: "Party Creation Six-Step Flow",
      imgOnRight: false,
    },
    {
      id: "3",
      numberTitle: "3. Dealer Booking",
      desc: "Dealers can review the party and game position, then request the assignment through a focused confirmation step while administrators retain control over final staffing decisions.",
      capabilities: ["Invitations", "Accept or decline", "Position booking"],
      img: "/images/event-staffing/event-staffing-feat-dealer-booking.png",
      alt: "Dealer Booking and Confirmation Modal",
      imgOnRight: true,
    },
    {
      id: "4",
      numberTitle: "4. Game and Position Management",
      desc: "Multiple games can be staffed independently, with live counts for confirmed, pending, open, and locked positions so administrators can immediately see where attention is needed.",
      capabilities: [
        "Quantities",
        "Open roles",
        "Status counts",
        "Game controls",
      ],
      img: "/images/event-staffing/event-staffing-feat-game-mgmt.png",
      alt: "Game and Position Management Table",
      imgOnRight: false,
    },
    {
      id: "5",
      numberTitle: "5. Roster Control",
      desc: "Administrators can review assigned dealers, update booking states, and manage staffing directly from the party roster without moving between disconnected screens.",
      capabilities: ["Confirmed", "Pending", "Open", "Locked"],
      img: "/images/event-staffing/event-staffing-feat-roster-control.png",
      alt: "Roster Control and Dealer Assignment Table",
      imgOnRight: true,
    },
    {
      id: "6",
      numberTitle: "6. Communications and Reporting",
      desc: "Search, filtering, message blasts, notifications, and exporting give operations teams a practical communication layer for reaching the right dealers without leaving the platform.",
      capabilities: ["Filters", "Message blasts", "Notifications", "Reports"],
      img: "/images/event-staffing/event-staffing-feat-communications.png",
      alt: "Communications, Message Blasts, and Reporting Dashboard",
      imgOnRight: false,
    },
    {
      id: "7",
      numberTitle: "7. Dealer Portal",
      desc: "Dealers get a dedicated portal for relevant parties, invitations, assignments, and event details, making it easier to understand what is available and what they have already booked.",
      capabilities: [
        "Available parties",
        "Invites",
        "Party details",
        "Upcoming work",
      ],
      img: "/images/event-staffing/event-staffing-feat-dealer-portal.png",
      alt: "Dealer Portal Interface and Party Invitations",
      imgOnRight: true,
    },
  ];

  return (
    <section className="es-key-features-wrapper">
      <div className="container-fluid horizontal-padding">
        {/* Key Features Header */}
        <div className="es-key-features-header">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="es-section-title">Key Features</h2>
          </motion.div>

          <motion.p
            className="es-key-features-intro"
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            The platform combines administrative control with a focused dealer
            portal. Its primary capabilities follow the workflows that operations
            teams and dealers use throughout each event lifecycle.
          </motion.p>
        </div>

        {/* 7 Feature Cards - All Constant Height and Width */}
        <div className="es-key-features-list">
          {features.map((feature, idx) => (
            <motion.div
              key={feature.id}
              className={`es-feature-card ${
                feature.imgOnRight ? "es-card-mockup-right" : "es-card-mockup-left"
              }`}
              initial={{ opacity: 0, y: 35 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, delay: idx * 0.1 }}
            >
              {feature.imgOnRight ? (
                <>
                  {/* Left: Text Content */}
                  <div className="es-card-content">
                    <h3 className="es-feature-num-title">
                      {feature.numberTitle}
                    </h3>
                    <p className="es-feature-card-desc">
                      - {feature.desc}
                    </p>
                    <div className="es-cap-section">
                      <div className="es-cap-title">Key capabilities:</div>
                      <div className="es-cap-tags">
                        {feature.capabilities.map((cap, cIdx) => (
                          <span key={cIdx} className="es-cap-tag">
                            <span className="es-cap-dot">•</span>
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right: Mockup Frame */}
                  <div className="es-card-mockup">
                    <div className="es-feature-device-frame">
                      <img
                        src={feature.img}
                        alt={feature.alt}
                        className="es-feature-mockup-img"
                        loading="lazy"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Left: Mockup Frame */}
                  <div className="es-card-mockup">
                    <div className="es-feature-device-frame">
                      <img
                        src={feature.img}
                        alt={feature.alt}
                        className="es-feature-mockup-img"
                        loading="lazy"
                      />
                    </div>
                  </div>

                  {/* Right: Text Content */}
                  <div className="es-card-content">
                    <h3 className="es-feature-num-title">
                      {feature.numberTitle}
                    </h3>
                    <p className="es-feature-card-desc">
                      - {feature.desc}
                    </p>
                    <div className="es-cap-section">
                      <div className="es-cap-title">Key capabilities:</div>
                      <div className="es-cap-tags">
                        {feature.capabilities.map((cap, cIdx) => (
                          <span key={cIdx} className="es-cap-tag">
                            <span className="es-cap-dot">•</span>
                            {cap}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
